import { useState, useEffect, useCallback } from "react";
import { adminService } from "@b2b/services/api/modules/admin/admin.service";
import { sellerService } from "@b2b/services/api/modules/seller/seller.service";
import { useAuth } from "../auth/Authprovider";
import { ProductStats, AdminProduct } from "../types/product.types";

export function useProductStats(role?: "admin" | "seller") {
  const { userRole, isLoading: authLoading, isAuthenticated } = useAuth();

  const actualRole = role || userRole || "admin";
  const isAdmin = actualRole === "admin";

  const [stats, setStats] = useState<ProductStats>({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    adminCreated: 0,
    sellerSubmitted: 0,
    pendingUpdates: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  const fetchStats = useCallback(
    async (signal?: AbortSignal, forceRefresh = false) => {
      try {
        setLoading(true);
        setError(null);

        let statsData: ProductStats;

        if (isAdmin) {
          // Use dedicated statistics endpoint for accurate counts
          if (typeof adminService.getAdminProductStatistics === "function") {
            try {
              const response =
                await adminService.getAdminProductStatistics(forceRefresh);

              if (signal?.aborted) return;

              if (response.success && response.data) {
                const stats = response.data;
                statsData = {
                  total: stats.total || 0,
                  pending: stats.byStatus?.pending || 0,
                  approved: stats.byStatus?.approved || 0,
                  rejected: stats.byStatus?.rejected || 0,
                  adminCreated: stats.byCreated?.adminCreated || 0,
                  sellerSubmitted: stats.byCreated?.sellerSubmitted || 0,
                  pendingUpdates: stats.pendingUpdates ?? 0,
                };
                setStats(statsData);
                return;
              }
            } catch (statsError) {
              console.warn(
                "Statistics endpoint failed, falling back to products list:",
                statsError
              );
            }
          }

          // Fallback to calculating from products list
          const productsResponse = await adminService.getAdminProducts(
            { limit: 1000 },
            forceRefresh
          );
          if (signal?.aborted) return;

          if (productsResponse.success) {
            const allProducts = productsResponse.data.products || [];
            statsData = {
              total: productsResponse.data.total || allProducts.length,
              pending: allProducts.filter(
                (p: AdminProduct) => p.approvalStatus === "pending"
              ).length,
              approved: allProducts.filter(
                (p: AdminProduct) => p.approvalStatus === "approved"
              ).length,
              rejected: allProducts.filter(
                (p: AdminProduct) => p.approvalStatus === "rejected"
              ).length,
              adminCreated: allProducts.filter(
                (p: AdminProduct) => p.adminCreated
              ).length,
              sellerSubmitted: allProducts.filter(
                (p: AdminProduct) => !p.adminCreated
              ).length,
              pendingUpdates: allProducts.filter(
                (p: AdminProduct) => p.hasPendingUpdate
              ).length,
            };
          } else {
            throw new Error("Failed to fetch statistics");
          }
        } else {
          // For sellers, use the existing seller products endpoint
          const response = await sellerService.getSellerProducts(
            {},
            forceRefresh
          );

          if (signal?.aborted) return;

          if (response.success) {
            const allProducts = response.data.products || [];
            statsData = {
              total: allProducts.length,
              pending: allProducts.filter(
                (p: AdminProduct) => p.approvalStatus === "pending"
              ).length,
              approved: allProducts.filter(
                (p: AdminProduct) => p.approvalStatus === "approved"
              ).length,
              rejected: allProducts.filter(
                (p: AdminProduct) => p.approvalStatus === "rejected"
              ).length,
              draft: allProducts.filter(
                (p: AdminProduct) => (p as any).status === "draft"
              ).length,
            };
          } else {
            throw new Error("Failed to fetch statistics");
          }
        }

        setStats(statsData);
      } catch (err) {
        if (!signal?.aborted) {
          console.error("Error fetching stats:", err);
          setError(
            err instanceof Error ? err.message : "Failed to fetch statistics"
          );
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [isAdmin]
  );

  const refresh = useCallback(async () => {
    if (abortController) {
      abortController.abort();
    }

    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    await fetchStats(newAbortController.signal, true);
  }, [abortController, fetchStats]);

  // Initial load
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;

    let isMounted = true;

    const initialLoad = async () => {
      const { getAccessToken } = await import("@b2b/services");
      const token = await getAccessToken();
      if (!token) {
        if (isMounted) {
          setTimeout(() => initialLoad(), 500);
        }
        return;
      }

      if (!isMounted) return;

      if (abortController) {
        abortController.abort();
      }

      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      try {
        await fetchStats(newAbortController.signal, true);
      } catch (error) {
        if (!newAbortController.signal.aborted && isMounted) {
          console.error("Error in initial load:", error);
        }
      }
    };

    initialLoad();

    return () => {
      isMounted = false;
    };
  }, [authLoading, isAuthenticated, fetchStats]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, []);

  return {
    stats,
    loading,
    error,
    refresh,
  };
}
